const SEO_MARKER = "data-welda-seo";

const LANG_FALLBACK = ["nl", "fr", "en"];

const {
  getProductImageData,
} = require("../utils/images");
const { perfionPictureSizes } = require("../types/images.types");
const {
  buildCategoryBreadcrumbItems,
} = require("./categoryBreadcrumb");

/** Perfion ERP fields that may contain supplier / cross-reference codes. */
const PERFION_ERP_SEO_FIELDS = [
  "ERPType",
  "Barcode",
  "BarcodeWELDA",
  "MainSupplierCodeOnItemSupplierList",
  "Ivalue",
  "IValueWELDA",
];

const PERFION_FLAT_IDENTIFIER_KEYS = [
  "ItemNumber",
  "ManufacturerPartNumber",
  "SupplierArticleNumber",
  "SupplierReference",
  "SupplierItemNumber",
  "OEMNumber",
  "CatalogNumber",
  "ReferenceNumber",
  "VendorPartNumber",
  "MPN",
  "EAN",
  "GTIN",
  "GTIN13",
  "GTIN14",
  "LevCode",
  "SupplierCode",
  "ERPType",
];

const stripHtml = (html) => {
  if (!html) {
    return "";
  }
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const truncate = (text, maxLength = 160) => {
  if (!text || text.length <= maxLength) {
    return text || "";
  }
  return `${text.slice(0, maxLength - 1).trim()}…`;
};

const addIdentifier = (set, value) => {
  if (value == null) {
    return;
  }
  const normalized = String(value).trim();
  if (normalized && normalized !== "0") {
    set.add(normalized);
  }
};

const collectFromObject = (set, object, keys) => {
  if (!object || typeof object !== "object") {
    return;
  }
  for (const key of keys) {
    addIdentifier(set, object[key]);
  }
};

const unwrapPerfionField = (field) => {
  if (field == null) {
    return undefined;
  }
  if (typeof field === "object" && "value" in field) {
    const { value } = field;
    if (Array.isArray(value)) {
      return value.filter(Boolean).join(", ");
    }
    return value;
  }
  return field;
};

const isFlattenedPerfion = (perfion) =>
  Boolean(
    perfion?.ItemName ||
      perfion?.ShortDescription ||
      perfion?.RemProductMainImageURL
  );

const getPerfionLangData = (perfion, language = "nl") => {
  if (!perfion || typeof perfion !== "object") {
    return null;
  }
  if (isFlattenedPerfion(perfion)) {
    return perfion;
  }
  for (const lang of [language, ...LANG_FALLBACK.filter((l) => l !== language)]) {
    if (perfion[lang]) {
      return perfion[lang];
    }
  }
  return null;
};

/**
 * Read a Perfion field from flat MMS output or nested nl/fr/en blocks.
 * @param {object} perfion
 * @param {string} language
 * @param {string[]} candidates dotted paths e.g. "MainInformation.ItemName"
 */
const readPerfionField = (perfion, language, candidates) => {
  const langData = getPerfionLangData(perfion, language);
  if (!langData) {
    return undefined;
  }

  for (const candidate of candidates) {
    if (langData[candidate] != null && !candidate.includes(".")) {
      const unwrapped = unwrapPerfionField(langData[candidate]);
      if (unwrapped != null && String(unwrapped).trim() !== "") {
        return unwrapped;
      }
    }

    const parts = candidate.split(".");
    let current = langData;
    for (const part of parts) {
      current = current?.[part];
    }
    const unwrapped = unwrapPerfionField(current);
    if (unwrapped != null && String(unwrapped).trim() !== "") {
      return unwrapped;
    }
  }

  return undefined;
};

const collectPerfionSeoIdentifiers = (set, perfion, language) => {
  if (!perfion) {
    return;
  }

  collectFromObject(set, perfion, PERFION_FLAT_IDENTIFIER_KEYS);

  const langData = getPerfionLangData(perfion, language);
  if (!langData || isFlattenedPerfion(perfion)) {
    return;
  }

  for (const lang of LANG_FALLBACK) {
    const block = perfion[lang];
    if (!block?.ERP) {
      continue;
    }
    for (const key of PERFION_ERP_SEO_FIELDS) {
      addIdentifier(set, unwrapPerfionField(block.ERP[key]));
    }
  }
};

const collectLeverancierIdentifiers = (set, product) => {
  if (!Array.isArray(product?.leverancier)) {
    return;
  }

  for (const supplier of product.leverancier) {
    addIdentifier(set, supplier?.levcode);
    addIdentifier(set, supplier?.lev_nr);
  }
};

/**
 * Supplier / OEM / alternate article numbers for structured data only.
 * These are not rendered in the page body — they live in JSON-LD for search engines.
 */
const collectCrossReferenceIdentifiers = (product, language = "nl") => {
  const identifiers = new Set();

  addIdentifier(identifiers, product?.Barcode);
  collectLeverancierIdentifiers(identifiers, product);

  const interCompanyEntries = Array.isArray(product?.inter_company)
    ? product.inter_company
    : product?.intercompany
      ? [product.intercompany]
      : [];

  for (const entry of interCompanyEntries) {
    collectFromObject(identifiers, entry, [
      "barcode",
      "levcode",
      "vorigartnr",
      "volgendartnr",
      "hoofdaankoper",
    ]);
  }

  collectFromObject(identifiers, product?.getPrice?.intercompany, ["levcode"]);

  if (Array.isArray(product?.AlternatieveArtikels)) {
    for (const alt of product.AlternatieveArtikels) {
      addIdentifier(identifiers, alt);
    }
  }

  if (Array.isArray(product?.AlternatieveArtikelsObj)) {
    for (const alt of product.AlternatieveArtikelsObj) {
      if (typeof alt === "string") {
        addIdentifier(identifiers, alt);
      } else {
        addIdentifier(identifiers, alt?.Nummer);
        addIdentifier(identifiers, alt?.artnr);
        addIdentifier(identifiers, alt?.levcode);
      }
    }
  }

  collectPerfionSeoIdentifiers(identifiers, product?.perfion_data, language);

  if (product?.Nummer) {
    identifiers.delete(String(product.Nummer).trim());
  }

  return [...identifiers];
};

const pickSupplierArticleNumber = (product) => {
  const primarySupplier = product?.leverancier?.[0];
  if (primarySupplier?.levcode) {
    return String(primarySupplier.levcode).trim();
  }

  const erpType = readPerfionField(product?.perfion_data, "nl", [
    "ERPType",
    "ERP.ERPType",
  ]);
  if (erpType != null) {
    return String(erpType).trim();
  }

  return undefined;
};

const pickManufacturerPartNumber = (product, crossRefs) => {
  const supplierArticle = pickSupplierArticleNumber(product);
  if (supplierArticle) {
    return supplierArticle;
  }

  const perfion = product?.perfion_data;
  for (const key of [
    "ManufacturerPartNumber",
    "MPN",
    "OEMNumber",
    "SupplierReference",
  ]) {
    const flat = unwrapPerfionField(perfion?.[key]);
    if (flat) {
      return String(flat).trim();
    }
  }

  for (const lang of LANG_FALLBACK) {
    for (const key of [
      "ManufacturerPartNumber",
      "MPN",
      "OEMNumber",
      "SupplierReference",
    ]) {
      const nested = unwrapPerfionField(perfion?.[lang]?.ERP?.[key]);
      if (nested) {
        return String(nested).trim();
      }
    }
  }

  return crossRefs[0] || undefined;
};

const resolveProductName = (product, language = "nl") => {
  const fromPerfion = readPerfionField(product?.perfion_data, language, [
    "ItemName",
    "MainInformation.ItemName",
    "ERP.ERPName",
  ]);

  return stripHtml(fromPerfion || product?.Naam || product?.Nummer || "");
};

const resolveProductDescription = (product, language = "nl") => {
  const fromPerfion = readPerfionField(product?.perfion_data, language, [
    "ShortDescription",
    "LongDescription",
    "MainInformation.ShortDescription",
    "MainInformation.LongDescription",
    "ERP.ERPName",
  ]);

  const brand = product?.Merk;
  const name = resolveProductName(product, language);

  return stripHtml(
    fromPerfion ||
      product?.Omschrijving ||
      (brand ? `${name} - ${brand}` : name)
  );
};

const resolveProductImage = (
  product,
  language = "nl",
  { dossier } = {}
) => {
  // Public SEO / crawlers: no watermark (shop UI still uses product=1).
  const images = resolveProductImages(product, language, {
    dossier,
    withoutWatermark: true,
  });
  return images[0];
};

const rewriteApiHost = (url, apiBaseUrl) => {
  if (!url || !apiBaseUrl) {
    return url;
  }
  try {
    const parsed = new URL(url, apiBaseUrl);
    if (!parsed.pathname.includes("/api/")) {
      return url;
    }
    const base = new URL(apiBaseUrl.endsWith("/") ? apiBaseUrl : `${apiBaseUrl}/`);
    return `${base.origin}${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
};

/**
 * Same image sources as ProductImagesBlock / getProductImageData (Perfion IDs, then CAC).
 * Falls back to legacy RemProductMainImageURL when present.
 */
const resolveProductImages = (
  product,
  language = "nl",
  {
    dossier,
    pictureSize = perfionPictureSizes.EIGHTHUNDRED,
    apiBaseUrl,
    withoutWatermark = false,
  } = {}
) => {
  // Shop search flattens perfion_data to the active language; SEO may still see nested langs.
  const langPerfion = getPerfionLangData(product?.perfion_data, language);
  const productForImages =
    langPerfion && product?.perfion_data !== langPerfion
      ? { ...product, perfion_data: langPerfion }
      : product;

  const fromShop = getProductImageData(
    productForImages,
    pictureSize,
    dossier,
    { withoutWatermark }
  )
    .map((entry) => entry?.imageUrl)
    .filter(Boolean)
    .map((url) => rewriteApiHost(url, apiBaseUrl));

  if (fromShop.length > 0) {
    return fromShop;
  }

  const legacy = readPerfionField(product?.perfion_data, language, [
    "Images.RemProductMainImageURL",
    "RemProductMainImageURL",
    "DocumentsAndMedia.RemProductMainImageURL",
  ]);

  if (!legacy) {
    return [];
  }

  return [String(legacy).replace("400x400", "800x800")];
};

const resolveProductCategory = (product, language = "nl") => {
  const category = readPerfionField(product?.perfion_data, language, [
    "Category",
    "MainInformation.Category",
    "ERP.ERPMainGroup",
  ]);

  if (Array.isArray(category)) {
    return category.join(" > ");
  }

  return category ? String(category) : undefined;
};

const normalizeGtin = (product, language = "nl") => {
  const candidates = [
    product?.Barcode,
    readPerfionField(product?.perfion_data, language, [
      "Barcode",
      "ERP.Barcode",
      "ERP.BarcodeWELDA",
    ]),
  ];

  for (const value of candidates) {
    if (!value) {
      continue;
    }
    const digits = String(value).replace(/\D/g, "");
    if (
      digits.length === 8 ||
      digits.length === 12 ||
      digits.length === 13 ||
      digits.length === 14
    ) {
      return digits;
    }
  }

  return undefined;
};

const resolveLiveArticleNetPriceInclVat = (product, liveArticle) => {
  if (!liveArticle?.Net || liveArticle.Net == 0) {
    return undefined;
  }

  const vatModifier = 1 + (product?.BTW ?? 0) / 100;
  const net = Number(liveArticle.Net);
  const inclVat = liveArticle.VATExcl ? net * vatModifier : net;

  if (!Number.isFinite(inclVat) || inclVat <= 0) {
    return undefined;
  }

  return Number(inclVat.toFixed(2));
};

function resolveProductPrice(product, liveArticle) {
  // When caller explicitly passes liveArticle (even undefined), do not fall back to MMS.
  // Must be a `function` (not arrow): arrow `arguments` would be the CommonJS module wrapper.
  if (arguments.length >= 2 && liveArticle === undefined) {
    return undefined;
  }

  if (liveArticle) {
    const livePrice = resolveLiveArticleNetPriceInclVat(product, liveArticle);
    if (livePrice != null) {
      return livePrice.toFixed(2);
    }
    return undefined;
  }

  const inclVat =
    product?.getPrice?.endPrice ??
    product?.prePromoPriceInclVAT ??
    (product?.Prijs != null && product?.BTW != null
      ? product.Prijs * (1 + product.BTW / 100)
      : null);

  if (inclVat != null && Number(inclVat) > 0) {
    return Number(inclVat).toFixed(2);
  }

  if (product?.Prijs != null && Number(product.Prijs) > 0) {
    return Number(product.Prijs).toFixed(2);
  }

  return undefined;
}

function resolveAvailability(product, liveArticle) {
  // Same `arguments` / arrow caveat as resolveProductPrice.
  if (arguments.length >= 2 && liveArticle === undefined) {
    return undefined;
  }

  if (product?.ArtikelStatusKort === "BLK") {
    return "https://schema.org/Discontinued";
  }

  if (liveArticle) {
    const stock = liveArticle.Stock;
    if (stock == null) {
      return undefined;
    }
    return Number(stock) > 0
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock";
  }

  const stock = product?.Voorraad;
  if (stock == null) {
    return "https://schema.org/InStock";
  }
  return Number(stock) > 0
    ? "https://schema.org/InStock"
    : "https://schema.org/OutOfStock";
}

const isBlockedShopArticle = (product) => product?.ArtikelStatusKort === "BLK";

const getResolvedReplacements = (product) =>
  Array.isArray(product?.resolvedReplacements)
    ? product.resolvedReplacements.filter((item) => item?.Nummer)
    : [];

const buildProductPageUrl = (siteBaseUrl, nummer) => {
  if (!siteBaseUrl || nummer == null || String(nummer).trim() === "") {
    return undefined;
  }
  return `${String(siteBaseUrl).replace(/\/$/, "")}/shop/product/${encodeURIComponent(
    String(nummer).trim()
  )}`;
};

const buildProductCanonicalUrl = (product, siteBaseUrl) => {
  const replacements = getResolvedReplacements(product);
  const nummer =
    isBlockedShopArticle(product) && replacements.length === 1
      ? replacements[0].Nummer
      : product?.Nummer;
  return buildProductPageUrl(siteBaseUrl, nummer);
};

const toAbsoluteUrl = (url, siteBaseUrl) => {
  if (!url) {
    return undefined;
  }
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  if (!siteBaseUrl) {
    return url;
  }
  const origin = siteBaseUrl.replace(/\/site\/\d+\/?$/, "").replace(/\/$/, "");
  return `${origin}${url.startsWith("/") ? url : `/${url}`}`;
};

const buildSupplierIdentifiersJsonLd = (product) => {
  const entries = [];

  if (Array.isArray(product?.leverancier)) {
    for (const supplier of product.leverancier) {
      if (supplier?.levcode) {
        entries.push({
          "@type": "PropertyValue",
          propertyID: "supplierArticleNumber",
          value: String(supplier.levcode).trim(),
          ...(supplier?.lev_naam
            ? { description: String(supplier.lev_naam).trim() }
            : {}),
        });
      }
      if (supplier?.lev_nr != null) {
        entries.push({
          "@type": "PropertyValue",
          propertyID: "supplierNumber",
          value: String(supplier.lev_nr).trim(),
          ...(supplier?.lev_naam
            ? { description: String(supplier.lev_naam).trim() }
            : {}),
        });
      }
    }
  }

  return entries;
};

const buildBreadcrumbListJsonLd = (breadcrumbItems) => {
  if (!Array.isArray(breadcrumbItems) || breadcrumbItems.length === 0) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbItems.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      ...(crumb.url ? { item: crumb.url } : {}),
    })),
  };
};

const escapeHtmlAttr = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/**
 * Build <head> snippets for server-side injection into SPA index.html.
 */
const renderSeoHeadHtml = (meta) => {
  if (!meta) {
    return "";
  }

  const parts = [];
  if (meta.title) {
    parts.push(`<title>${escapeHtmlAttr(meta.title)}</title>`);
  }
  if (meta.description) {
    parts.push(
      `<meta name="description" content="${escapeHtmlAttr(meta.description)}" ${SEO_MARKER}="">`
    );
  }
  if (meta.robots) {
    parts.push(
      `<meta name="robots" content="${escapeHtmlAttr(meta.robots)}" ${SEO_MARKER}="">`
    );
  }
  if (meta.canonicalUrl) {
    parts.push(
      `<link rel="canonical" href="${escapeHtmlAttr(meta.canonicalUrl)}" ${SEO_MARKER}="">`
    );
  }

  const openGraph = meta.openGraph || {};
  for (const [key, content] of Object.entries({
    "og:type": openGraph.type,
    "og:title": openGraph.title,
    "og:description": openGraph.description,
    "og:url": openGraph.url,
    "og:image": openGraph.image,
    "og:site_name": openGraph.siteName,
  })) {
    if (content) {
      parts.push(
        `<meta property="${key}" content="${escapeHtmlAttr(content)}" ${SEO_MARKER}="">`
      );
    }
  }

  const twitter = meta.twitter || {};
  for (const [key, content] of Object.entries({
    "twitter:card": twitter.card,
    "twitter:title": twitter.title,
    "twitter:description": twitter.description,
    "twitter:image": twitter.image,
  })) {
    if (content) {
      parts.push(
        `<meta name="${key}" content="${escapeHtmlAttr(content)}" ${SEO_MARKER}="">`
      );
    }
  }

  const jsonLdBlocks = Array.isArray(meta.jsonLd)
    ? meta.jsonLd
    : meta.jsonLd
      ? [meta.jsonLd]
      : [];

  for (const block of jsonLdBlocks) {
    parts.push(
      `<script type="application/ld+json" ${SEO_MARKER}="">${JSON.stringify(block)}</script>`
    );
  }

  return parts.join("\n    ");
};

const injectSeoIntoHtml = (html, meta) => {
  if (!html || !meta) {
    return html;
  }

  const headHtml = renderSeoHeadHtml(meta);
  if (!headHtml) {
    return html;
  }

  let result = html;
  if (meta.title) {
    result = result.replace(
      /<title>[^<]*<\/title>/i,
      `<title>${escapeHtmlAttr(meta.title)}</title>`
    );
  }

  if (/<\/head>/i.test(result)) {
    return result.replace(/<\/head>/i, `    ${headHtml}\n  </head>`);
  }

  return `${result}\n${headHtml}`;
};

const buildProductJsonLd = ({
  product,
  liveArticle = undefined,
  canonicalUrl,
  siteBaseUrl,
  companyName,
  language,
  dossier,
  apiBaseUrl,
  categoryPath,
}) => {
  const name = resolveProductName(product, language);
  const description = truncate(resolveProductDescription(product, language), 5000);
  const crossRefs = collectCrossReferenceIdentifiers(product, language);
  const mpn = pickManufacturerPartNumber(product, crossRefs);
  const gtin = normalizeGtin(product, language);
  // Crawler-facing JSON-LD must not use watermarked URLs (Merchant picks these up).
  // On-page shop <img> tags keep product=1 separately.
  const images = resolveProductImages(product, language, {
    dossier,
    apiBaseUrl,
    withoutWatermark: true,
  })
    .map((url) => toAbsoluteUrl(url, siteBaseUrl))
    .filter(Boolean);
  const price = resolveProductPrice(product, liveArticle);
  const brandName =
    product?.Merk ||
    readPerfionField(product?.perfion_data, language, ["ERP.ERPManufacturer"]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    sku: product?.Nummer,
    url: canonicalUrl,
    inLanguage: language,
  };

  if (images.length === 1) {
    jsonLd.image = images[0];
  } else if (images.length > 1) {
    jsonLd.image = images;
  }
  if (brandName) {
    jsonLd.brand = { "@type": "Brand", name: String(brandName).trim() };
  }
  if (mpn) {
    jsonLd.mpn = mpn;
  }
  if (gtin) {
    jsonLd.gtin13 = gtin;
  }

  const alternateNames = crossRefs.filter((id) => id !== mpn);
  if (alternateNames.length > 0) {
    jsonLd.alternateName =
      alternateNames.length === 1 ? alternateNames[0] : alternateNames;
  }

  const supplierIdentifiers = buildSupplierIdentifiersJsonLd(product);
  const genericIdentifiers = crossRefs
    .filter(
      (value) =>
        !supplierIdentifiers.some((entry) => entry.value === value)
    )
    .map((value) => ({
      "@type": "PropertyValue",
      propertyID: "crossReferenceArticleNumber",
      value,
    }));

  const allIdentifiers = [...supplierIdentifiers, ...genericIdentifiers];
  if (allIdentifiers.length > 0) {
    jsonLd.identifier = allIdentifiers;
  }

  if (price) {
    const availability = resolveAvailability(product, liveArticle);
    jsonLd.offers = {
      "@type": "Offer",
      url: canonicalUrl,
      priceCurrency: "EUR",
      price,
      ...(availability ? { availability } : {}),
      seller: {
        "@type": "Organization",
        name: companyName,
      },
    };
  }

  const category =
    categoryPath || resolveProductCategory(product, language) || undefined;
  if (category) {
    jsonLd.category = category;
  }

  return jsonLd;
};

const buildProductSeoMeta = ({
  product,
  liveArticle = undefined,
  canonicalUrl,
  siteBaseUrl,
  companyName,
  language,
  dossier,
  apiBaseUrl,
  categories,
  breadcrumbItems: breadcrumbItemsInput,
}) => {
  const productName = resolveProductName(product, language);
  const brand =
    product?.Merk ||
    readPerfionField(product?.perfion_data, language, ["ERP.ERPManufacturer"]);
  const titleParts = [productName, brand, companyName].filter(Boolean);
  const title = titleParts.join(" | ");
  const description = truncate(resolveProductDescription(product, language), 160);
  // og:image / twitter:image — clean for crawlers; shop UI stays watermarked.
  const images = resolveProductImages(product, language, {
    dossier,
    apiBaseUrl,
    withoutWatermark: true,
  })
    .map((url) => toAbsoluteUrl(url, siteBaseUrl))
    .filter(Boolean);
  const image = images[0];
  const crossRefs = collectCrossReferenceIdentifiers(product, language);

  const categoryIds =
    product?.perfion_data?.MainInformation?.Category?.value ??
    readPerfionField(product?.perfion_data, language, [
      "Category",
      "MainInformation.Category",
    ]);

  const breadcrumbItems =
    breadcrumbItemsInput ||
    buildCategoryBreadcrumbItems({
      categories,
      categoryIds,
      language,
      shopBaseUrl: siteBaseUrl ? `${siteBaseUrl.replace(/\/$/, "")}/shop` : undefined,
      productCanonicalUrl: canonicalUrl,
      productName,
    });

  const categoryPath = breadcrumbItems
    .filter((item) => item.name && item.name !== "Shop" && item.url !== canonicalUrl)
    .map((item) => item.name)
    .join(" > ");

  const productJsonLd = buildProductJsonLd({
    product,
    liveArticle,
    canonicalUrl,
    siteBaseUrl,
    companyName,
    language,
    dossier,
    apiBaseUrl,
    categoryPath: categoryPath || undefined,
  });

  const breadcrumbJsonLd = buildBreadcrumbListJsonLd(breadcrumbItems);
  const jsonLd = breadcrumbJsonLd
    ? [productJsonLd, breadcrumbJsonLd]
    : productJsonLd;

  return {
    title,
    description,
    canonicalUrl,
    robots: "index,follow",
    openGraph: {
      type: "product",
      title,
      description,
      url: canonicalUrl,
      image,
      siteName: companyName,
    },
    twitter: {
      card: image ? "summary_large_image" : "summary",
      title,
      description,
      image,
    },
    jsonLd,
    breadcrumbItems,
    crossReferenceCount: crossRefs.length,
  };
};

const looksLikeI18nKey = (value) =>
  typeof value === "string" && /^frontend\.[a-z0-9_.-]+$/i.test(value.trim());

const pickProductGroupDisplayText = (...candidates) => {
  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }
    const text = stripHtml(candidate);
    if (!text || looksLikeI18nKey(text)) {
      continue;
    }
    return text;
  }
  return "";
};

const buildProductGroupSeoMeta = ({
  productGroup,
  canonicalUrl,
  companyName,
  title: titleOverride,
  description: descriptionOverride,
}) => {
  const displayName = pickProductGroupDisplayText(
    titleOverride,
    productGroup?.label,
    productGroup?.name,
    productGroup?.title
  );
  const title = [displayName, companyName].filter(Boolean).join(" | ");
  const description = truncate(
    pickProductGroupDisplayText(
      descriptionOverride,
      productGroup?.label,
      productGroup?.name
    ),
    160
  );

  return {
    title,
    description,
    canonicalUrl,
    robots: "index,follow",
    openGraph: {
      type: "website",
      title,
      description,
      url: canonicalUrl,
      siteName: companyName,
    },
    twitter: {
      card: "summary",
      title,
      description,
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: displayName,
      description,
      url: canonicalUrl,
    },
  };
};

module.exports = {
  SEO_MARKER,
  buildBreadcrumbListJsonLd,
  buildCategoryBreadcrumbItems,
  buildProductCanonicalUrl,
  buildProductGroupSeoMeta,
  buildProductPageUrl,
  buildProductSeoMeta,
  collectCrossReferenceIdentifiers,
  getResolvedReplacements,
  injectSeoIntoHtml,
  isBlockedShopArticle,
  normalizeGtin,
  pickManufacturerPartNumber,
  renderSeoHeadHtml,
  resolveAvailability,
  resolveProductCategory,
  resolveProductDescription,
  resolveProductName,
  resolveProductImage,
  resolveProductImages,
  resolveProductPrice,
  stripHtml,
  toAbsoluteUrl,
  truncate,
};
